import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResumeDropzone = ({ onUploadComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadState, setUploadState] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const acceptedTypes = '.pdf,.docx,.doc,.txt';

  const processFile = async (selectedFile) => {
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'doc', 'txt'].includes(ext)) {
      setErrorMsg('Unsupported file type. Please upload PDF, DOCX, or TXT.');
      setUploadState('error'); return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setErrorMsg('File too large. Max 10MB allowed.');
      setUploadState('error'); return;
    }
    setFile(selectedFile); setUploadState('uploading'); setUploadProgress(0); setErrorMsg('');
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
      });
      setUploadState('success'); setUploadProgress(100);
      setTimeout(() => onUploadComplete(response.data), 800);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Upload failed. Please try again.');
      setUploadState('error'); setUploadProgress(0);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault(); setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) processFile(dropped);
  };
  const handleFileInput = (e) => {
    const selected = e.target.files[0];
    if (selected) processFile(selected);
  };
  const handleBoxClick = () => {
    if ((uploadState === 'idle' || uploadState === 'error') && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const resetDropzone = (e) => {
    e.stopPropagation();
    setFile(null); setUploadState('idle'); setUploadProgress(0); setErrorMsg('');
  };

  const borderColor = isDragOver ? '#E8A020'
    : uploadState === 'error' ? '#D94040'
    : uploadState === 'success' ? '#3FA86A'
    : 'rgba(0,0,0,0.13)';

  return (
    <div data-testid="resume-dropzone" style={{ width: '100%' }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />

      <AnimatePresence mode="wait">
        {uploadState === 'idle' || uploadState === 'error' ? (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            onClick={handleBoxClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              backgroundColor: isDragOver ? '#FFF8EC' : '#fafafa',
              border: `1.5px dashed ${borderColor}`,
              borderRadius: '12px',
              padding: '36px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
              boxShadow: isDragOver ? '0 0 0 3px rgba(232,160,32,0.12)' : 'none',
              userSelect: 'none',
            }}
          >
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              backgroundColor: isDragOver ? 'rgba(232,160,32,0.12)' : '#f0f0f0',
              border: `1px solid ${isDragOver ? 'rgba(232,160,32,0.25)' : 'rgba(0,0,0,0.08)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
              transition: 'background-color 0.2s ease',
            }}>
              <Upload size={22} color={uploadState === 'error' ? '#D94040' : isDragOver ? '#E8A020' : 'rgba(0,0,0,0.45)'} />
            </div>

            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600,
              color: '#111111', marginBottom: '5px',
            }}>Drag or drop your resume here</p>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
              color: 'rgba(0,0,0,0.40)', marginBottom: '20px',
            }}>PDF, DOCX, or TXT · Max 10MB</p>

            {uploadState === 'error' && errorMsg && (
              <div style={{
                backgroundColor: 'rgba(217,64,64,0.06)', border: '1px solid rgba(217,64,64,0.20)',
                borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
                display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left',
              }}>
                <AlertCircle size={15} color="#D94040" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '14px', color: '#D94040', fontFamily: 'DM Sans, sans-serif' }}>{errorMsg}</span>
              </div>
            )}

            {/* Visual browse button — clicking anywhere on box already triggers file picker */}
            <div
              data-testid="resume-browse-button"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                padding: '10px 20px',
                backgroundColor: '#ffffff',
                border: '1.5px solid rgba(0,0,0,0.15)',
                borderRadius: '9999px',
                fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 500,
                color: '#111111',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                pointerEvents: 'none', // box click handles it
              }}
            >
              <FileText size={14} />
              Browse Files
            </div>
          </motion.div>

        ) : uploadState === 'uploading' ? (
          <motion.div key="uploading" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
            style={{
              backgroundColor: '#ffffff', border: '1.5px solid rgba(232,160,32,0.35)',
              borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <FileText size={20} color="#E8A020" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p data-testid="resume-file-name" style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 500,
                  color: '#111111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{file?.name}</p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.40)' }}>Uploading...</p>
              </div>
            </div>
            <div data-testid="resume-upload-progress" style={{
              height: '4px', backgroundColor: '#f0f0f0', borderRadius: '9999px', overflow: 'hidden',
            }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} transition={{ duration: 0.3 }}
                style={{ height: '100%', background: 'linear-gradient(90deg, #8EE5B4, #5BBF80, #3FA86A)', borderRadius: '9999px' }}
              />
            </div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.35)', marginTop: '6px', textAlign: 'right' }}>{uploadProgress}%</p>
          </motion.div>

        ) : (
          <motion.div key="success" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.25 }}
            onClick={handleBoxClick}
            style={{
              backgroundColor: '#ffffff', border: '1.5px solid rgba(63,168,106,0.40)',
              borderRadius: '12px', padding: '18px 20px',
              display: 'flex', alignItems: 'center', gap: '12px',
              boxShadow: '0 1px 4px rgba(63,168,106,0.08)', cursor: 'pointer',
            }}
          >
            <CheckCircle size={22} color="#3FA86A" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 500,
                color: '#111111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{file?.name}</p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: '#3FA86A' }}>Resume uploaded successfully</p>
            </div>
            <button data-testid="resume-remove-button" onClick={resetDropzone}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                color: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center',
              }}
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
